/**
 * The enum-to-tone maps, in one place.
 *
 * Each is a product decision with a comment attached, and scattering them across call sites is
 * how they drift. The labels for vocabulary values come from `GET /meta`, never from a map here
 * — only the tone is decided locally.
 */

import { Pill } from '~/ui/Pill';
import type { Tone } from '~/ui/tone';

/** A rename is not a third state: a renamed register is still active under its new name. */
export function StatusPill({ status }: { status: 'active' | 'dead' }) {
  return status === 'active' ? (
    <Pill tone="done" glyph="●">
      Active
    </Pill>
  ) : (
    <Pill tone="neutral" glyph="×">
      Dead
    </Pill>
  );
}

const QUEUE_TONE: Record<string, Tone> = {
  queued: 'neutral',
  running: 'quest',
  done: 'done',
  failed: 'issue',
  cancelled: 'neutral',
};

const QUEUE_LABEL: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export function QueueStatePill({ state }: { state: string }) {
  return <Pill tone={QUEUE_TONE[state] ?? 'neutral'}>{QUEUE_LABEL[state] ?? state}</Pill>;
}

/**
 * The server computed this against the register's own official-source list. The client never
 * derives it (`contradictions.md` #9). `unparseable` is a technical failure to read the address
 * and is deliberately not folded into `unofficial`.
 */
export function SourceStatusPill({ status }: { status: 'official' | 'unofficial' | 'unparseable' }) {
  if (status === 'official') return <Pill tone="done">Official source</Pill>;
  if (status === 'unparseable') return <Pill tone="issue">Address could not be read</Pill>;
  return <Pill tone="neutral">Unofficial source</Pill>;
}

const CHANGE_KIND: Record<string, { tone: Tone; label: string }> = {
  field: { tone: 'quest', label: 'Changed' },
  version: { tone: 'sugg', label: 'Added version' },
  rule: { tone: 'sugg', label: 'Added rule' },
  commencement: { tone: 'quest', label: 'Commencement' },
  adoption: { tone: 'quest', label: 'Adoption' },
  repeal: { tone: 'quest', label: 'Repeal' },
  version_end: { tone: 'quest', label: 'Version end' },
  version_removed: { tone: 'issue', label: 'Removed version' },
  citation: { tone: 'neutral', label: 'Citation' },
};

export function ChangeKindPill({ kind }: { kind: string }) {
  const meta = CHANGE_KIND[kind] ?? { tone: 'neutral' as Tone, label: kind };
  return <Pill tone={meta.tone}>{meta.label}</Pill>;
}

/**
 * Two badges rather than one, because they mean different work: `Never researched` is "start",
 * `Pending research` is "check again". Both are server-computed; the client never compares a
 * date to decide either.
 */
export function ResearchStatusBadge({ status }: { status: string }) {
  if (status === 'never_researched') return <Pill tone="neutral">Never researched</Pill>;
  if (status === 'pending_research') return <Pill tone="pend">Pending research</Pill>;
  return null;
}

const VERIFICATION: Record<string, { tone: Tone; label: string }> = {
  unverified: { tone: 'neutral', label: 'Unverified' },
  ai_proposed: { tone: 'quest', label: 'AI proposed' },
  human_confirmed: { tone: 'done', label: 'Human confirmed' },
  double_checked: { tone: 'done', label: 'Double checked' },
  contradicted: { tone: 'issue', label: 'Contradicted' },
};

export function VerificationPill({ state }: { state: string }) {
  const meta = VERIFICATION[state] ?? { tone: 'neutral' as Tone, label: state };
  return (
    <Pill tone={meta.tone} size="mini">
      {meta.label}
    </Pill>
  );
}

const EVIDENCE: Record<string, { tone: Tone; label: string }> = {
  official: { tone: 'done', label: 'Official evidence' },
  mixed: { tone: 'neutral', label: 'Mixed evidence' },
  unofficial_only: { tone: 'open', label: 'Unofficial evidence only' },
};

export function EvidenceQualityPill({ quality }: { quality: string }) {
  const meta = EVIDENCE[quality];
  if (!meta) return null;
  return (
    <Pill tone={meta.tone} size="mini">
      {meta.label}
    </Pill>
  );
}
